import React, { useState } from 'react';
import { useApp, ROLE_LABELS } from '../../context/AppContext';
import { Bell, Check, Search, Menu } from 'lucide-react';
import { Badge } from '../ui/badge';

export const TopBar = ({ title, onMenuClick, isMobile }) => {
  const { currentUser, notifications, markNotifRead, markAllRead } = useApp();
  const [showNotifs, setShowNotifs] = useState(false);
  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <header data-testid="top-bar" className="h-16 bg-white border-b border-border/60 flex items-center justify-between px-8 sticky top-0 z-40">
      <div className="flex items-center gap-4">
        {isMobile && (
          <button data-testid="hamburger-btn" onClick={onMenuClick} className="p-2 rounded-lg hover:bg-slate-100 transition-colors">
            <Menu className="w-5 h-5 text-slate-700" />
          </button>
        )}
        <h2 className="text-xl font-bold text-slate-900">{title}</h2>
      </div>

      <div className="flex items-center gap-4">
        {/* Search */}
        <div className="relative hidden md:block">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="global-search"
            type="text"
            placeholder="Search..."
            className="pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:border-amber-400 focus:ring-1 focus:ring-amber-400/20 outline-none transition-all w-60"
          />
        </div>

        {/* Notification Bell */}
        <div className="relative">
          <button
            data-testid="notification-bell"
            onClick={() => setShowNotifs(!showNotifs)}
            className="relative p-2 rounded-lg hover:bg-slate-50 transition-colors"
          >
            <Bell className="w-5 h-5 text-slate-600" strokeWidth={1.5} />
            {unreadCount > 0 && (
              <span className="notification-dot" />
            )}
          </button>

          {showNotifs && (
            <div data-testid="notification-panel" className="absolute right-0 top-12 w-80 bg-white border border-border rounded-xl shadow-lg overflow-hidden z-50">
              <div className="p-4 border-b border-border/40 flex items-center justify-between">
                <h3 className="text-sm font-bold text-slate-900">Notifications</h3>
                {unreadCount > 0 && (
                  <button
                    data-testid="mark-all-read-btn"
                    onClick={markAllRead}
                    className="text-xs text-amber-600 hover:text-amber-700 font-medium"
                  >
                    Mark all read
                  </button>
                )}
              </div>
              <div className="max-h-72 overflow-y-auto">
                {notifications.length === 0 ? (
                  <p className="p-4 text-sm text-slate-400 text-center">No notifications</p>
                ) : (
                  notifications.map(n => (
                    <div
                      key={n.id}
                      data-testid={`notification-${n.id}`}
                      className={`p-3 border-b border-border/20 flex items-start gap-3 hover:bg-slate-50 transition-colors cursor-pointer ${!n.read ? 'bg-amber-50/40' : ''}`}
                      onClick={() => !n.read && markNotifRead(n.id)}
                    >
                      <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${!n.read ? 'bg-amber-500' : 'bg-slate-200'}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-slate-700">{n.message}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0">{n.notif_type}</Badge>
                        </div>
                      </div>
                      {!n.read && (
                        <Check className="w-4 h-4 text-slate-300 flex-shrink-0 mt-0.5" />
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* User */}
        {currentUser && (
          <div data-testid="user-info" className="flex items-center gap-2 pl-3 border-l border-border/40">
            <div className="w-8 h-8 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center font-bold text-xs">
              {currentUser.name?.charAt(0)}
            </div>
            <div className="hidden lg:block">
              <p className="text-sm font-semibold text-slate-900 leading-none">{currentUser.name}</p>
              <p className="text-[11px] text-slate-400">{ROLE_LABELS[currentUser.role]}</p>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};
